import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Flame, Award, Lock, ArrowRight, Compass, Heart, Target, Star, Crown } from "lucide-react";
import AppShell from "@/components/shell/AppShell";
import XpPanel from "@/components/dashboard/XpPanel";
import RewardStore from "@/components/rewards/RewardStore";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { fetchMyXp } from "@/lib/xp";

type Stats = { challenges: number; streak: number; best: number; xp: number; assessments: number; saved: number; todayDone: boolean };

const BADGES = [
  { key: "first_step", name: "First step", hint: "Finish your first daily challenge", icon: Zap, done: (s: Stats) => s.challenges >= 1 },
  { key: "streak3", name: "On a roll", hint: "Keep a 3-day streak", icon: Flame, done: (s: Stats) => s.best >= 3 },
  { key: "streak7", name: "Week strong", hint: "Keep a 7-day streak", icon: Flame, done: (s: Stats) => s.best >= 7 },
  { key: "ten", name: "Habit builder", hint: "Finish 10 daily challenges", icon: Target, done: (s: Stats) => s.challenges >= 10 },
  { key: "discover", name: "Self-discoverer", hint: "Complete the assessment", icon: Compass, done: (s: Stats) => s.assessments >= 1 },
  { key: "saver", name: "Career collector", hint: "Save 3 careers you like", icon: Heart, done: (s: Stats) => s.saved >= 3 },
  { key: "xp500", name: "Rising star", hint: "Earn 500 XP", icon: Star, done: (s: Stats) => s.xp >= 500 },
  { key: "streak30", name: "Unstoppable", hint: "Keep a 30-day streak", icon: Crown, done: (s: Stats) => s.best >= 30 },
];

export default function Rewards() {
  const { user } = useAuth();
  const [s, setS] = useState<Stats | null>(null);

  useEffect(() => {
    if (!user) return;
    const today = new Date().toISOString().slice(0, 10);
    Promise.all([
      fetchMyXp(user.id),
      supabase.from("daily_challenges").select("challenge_date", { count: "exact" }).eq("user_id", user.id).eq("completed", true),
      supabase.from("assessments").select("id", { count: "exact", head: true }).eq("user_id", user.id).in("status", ["completed", "scored"]),
      supabase.from("saved_careers").select("id", { count: "exact", head: true }).eq("user_id", user.id),
    ]).then(([xp, ch, as, sv]) => {
      setS({
        xp: xp.totalXp, streak: xp.currentStreak, best: Math.max(xp.longestStreak, xp.currentStreak),
        challenges: ch.count ?? 0, assessments: as.count ?? 0, saved: sv.count ?? 0,
        todayDone: (ch.data ?? []).some((r: any) => r.challenge_date === today),
      });
    });
  }, [user]);

  const earned = s ? BADGES.filter((b) => b.done(s)).length : 0;

  return (
    <AppShell>
      <main className="mx-auto max-w-3xl space-y-8 px-4 py-8">
        <header className="animate-fade-in">
          <h1 className="font-display text-3xl font-bold text-foreground">Your rewards</h1>
          <p className="mt-2 text-muted-foreground">Small steps, every day. Here's how far you've come.</p>
        </header>

        <Card className="glass-card animate-fade-in overflow-hidden rounded-3xl">
          <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">Today's challenge</p>
              <h2 className="mt-1 font-display text-xl font-bold text-foreground">
                {s?.todayDone ? "Done for today — nice work." : "30 seconds to grow a little."}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {s?.todayDone ? "Come back tomorrow to keep your streak going." : "Finish it to earn XP and keep your streak."}
              </p>
            </div>
            <Button asChild className="h-12 rounded-2xl hover-scale">
              <Link to="/challenges">{s?.todayDone ? "See past challenges" : "Start now"} <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" /></Link>
            </Button>
          </CardContent>
        </Card>

        <XpPanel />

        <section aria-labelledby="badges-h">
          <div className="mb-4 flex items-baseline justify-between">
            <h2 id="badges-h" className="font-display text-xl font-bold text-foreground">Badges</h2>
            <span className="text-sm text-muted-foreground">{earned} of {BADGES.length} earned</span>
          </div>
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {BADGES.map((b, i) => {
              const got = s ? b.done(s) : false;
              const Icon = got ? b.icon : Lock;
              return (
                <li key={b.key} style={{ animationDelay: `${i * 50}ms` }}
                  className={`glass-card animate-scale-in rounded-3xl p-4 text-center ${got ? "" : "opacity-60"}`}>
                  <div className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${got ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <p className="mt-3 text-sm font-semibold text-foreground">{b.name}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{got ? "Earned" : b.hint}</p>
                </li>
              );
            })}
          </ul>
        </section>

        <RewardStore
          badgeNames={Object.fromEntries(BADGES.map((b) => [b.key, b.name]))}
          earnedBadge={(key) => {
            const b = BADGES.find((x) => x.key === key);
            return Boolean(s && b?.done(s));
          }}
        />

        <Link to="/leaderboard" className="flex items-center justify-center gap-2 text-sm text-primary hover:underline">
          <Award className="h-4 w-4" aria-hidden="true" /> See the community leaderboards
        </Link>
      </main>
    </AppShell>
  );
}
